import * as stylex from "@stylexjs/stylex";
import { useForm } from "@tanstack/react-form";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { SignInCard } from "@animus/dom-features";
import { useCookieAuth } from "@animus/frontend-features/auth-provider-cookie";

import { button, form } from "../../styles.stylex";

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const { signIn, isLoading, error } = useCookieAuth();
  const [isPending, setIsPending] = useState(false);

  const formState = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      setIsPending(true);
      try {
        await signIn(value.email, value.password);
        navigate({ to: search.redirect ?? "/" });
      } finally {
        setIsPending(false);
      }
    },
  });

  return (
    <SignInCard>
      <SignInCard.Header title="Sign In" />
      <SignInCard.Error message={error?.message} />
      <SignInCard.Content>
        <form
          {...stylex.props(form.space)}
          onSubmit={(e) => {
            e.preventDefault();
            formState.handleSubmit();
          }}
        >
          <formState.Field
            name="email"
            validators={{
              onChange: ({ value }) => {
                if (value) {
                  // biome-ignore lint/performance/useTopLevelRegex: TODO: better validation
                  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return;
                  }

                  return "Invalid email address";
                }

                return "Email is required";
              },
            }}
          >
            {(field) => (
              <div>
                <label {...stylex.props(form.label)} htmlFor={field.name}>
                  Email
                </label>
                <input
                  {...stylex.props(form.input)}
                  autoComplete="email"
                  id={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="email"
                  value={field.state.value}
                />
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p {...stylex.props(form.error)}>
                      {field.state.meta.errors.join(", ")}
                    </p>
                  )}
              </div>
            )}
          </formState.Field>

          <formState.Field
            name="password"
            validators={{
              onChange: ({ value }) =>
                value ? undefined : "Password is required",
            }}
          >
            {(field) => (
              <div>
                <label {...stylex.props(form.label)} htmlFor={field.name}>
                  Password
                </label>
                <input
                  {...stylex.props(form.input)}
                  autoComplete="current-password"
                  id={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="password"
                  value={field.state.value}
                />
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p {...stylex.props(form.error)}>
                      {field.state.meta.errors.join(", ")}
                    </p>
                  )}
              </div>
            )}
          </formState.Field>

          <formState.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <button
                {...stylex.props(button.base, button.primary, button.full)}
                disabled={isSubmitting || isPending || isLoading}
                type="submit"
              >
                {isSubmitting || isPending ? "Signing in..." : "Sign In"}
              </button>
            )}
          </formState.Subscribe>
        </form>
      </SignInCard.Content>
      <SignInCard.Footer>
        Don't have an account?{" "}
        <Link {...stylex.props(button.link)} to="/signup">
          Sign up
        </Link>
      </SignInCard.Footer>
    </SignInCard>
  );
}
